import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Database } from '@/types/database.types';
import { getAuthHeaders } from '@/utils/auth';

type Project = Database['public']['Tables']['projects']['Row'];
type ProjectTeam = Database['public']['Tables']['projects_team']['Row'];
type Member = Database['public']['Tables']['project_members']['Row'];
type ProjectWithTeamAndMembers = Project & {
  projects_team: ProjectTeam;
} & { project_members: Member[] | null };

export default function DeleteProjectDialog({
  project,
}: {
  project: ProjectWithTeamAndMembers;
}) {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameMatches = confirmName === project.project_name;

  const reset = () => {
    setConfirmName('');
    setError(null);
    setIsDeleting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (isDeleting) return;
    setOpen(next);
    if (!next) reset();
  };

  const handleDelete = async () => {
    if (!nameMatches || isDeleting) return;

    setIsDeleting(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `/api/engine/projects/${project.id}/hard-delete`,
        {
          method: 'DELETE',
          headers,
          body: JSON.stringify({ confirmName }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        toast.success('Project deleted successfully');
        navigate('/engine');
        return;
      }

      if (response.status === 404) {
        toast.info('Project already deleted');
        navigate('/engine');
        return;
      }

      if (data.phase === 'external-cleanup') {
        setError('External cleanup failed. Please try again later.');
      } else if (data.phase === 'database-cleanup') {
        setError('Database cleanup failed. Please try again later.');
      } else {
        setError(data.error ?? 'Something went wrong. Please try again.');
      }
    } catch (_err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={'destructive'}
          className="cursor-pointer w-100"
          size={'lg'}
        >
          Delete Project
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this project?</DialogTitle>
          <DialogDescription>
            This will permanently delete the project, all files and review
            links. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="confirm-name" className="pb-2">
            Type{' '}
            <span className="font-semibold text-red-500">
              {project.project_name}
            </span>{' '}
            to confirm.
          </Label>
          <Input
            id="confirm-name"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            autoComplete="off"
            disabled={isDeleting}
          />
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            variant={'outline'}
            onClick={() => handleOpenChange(false)}
            disabled={isDeleting}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            variant={'destructive'}
            onClick={handleDelete}
            disabled={!nameMatches || isDeleting}
            className="cursor-pointer"
          >
            {isDeleting ? 'Deleting...' : 'Delete Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
